/**
 * 超管「ディーラー」Tab —— /dealers 默认 Tab。
 * KPI 4 卡 + 统一代理店表（含 contact_email / 状态 / 期 KPI / 操作）。
 * 时间窗口可选：当月 / 指定月 / 全期間。
 */
import { ActionIcon, Badge, Card, Group, Select, SimpleGrid, Table, Text, Title } from '@mantine/core';
import { IconBuildingStore, IconCalendar, IconCoin, IconEdit, IconReceipt, IconTrendingUp } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getDealersOverview, type OverviewQuery, type OverviewResponse } from '@mygpt/app/apis/services/dealer';

const fmt = (n: number) => n.toLocaleString('ja-JP');

const KpiCard = ({
  label,
  value,
  icon,
  yen,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  yen?: boolean;
}) => (
  <Card withBorder>
    <Group gap="xs" mb={6}>
      {icon}
      <Text size="sm" c="dimmed">
        {label}
      </Text>
    </Group>
    <Title order={2}>
      {yen ? '¥' : ''}
      {fmt(value)}
    </Title>
  </Card>
);

interface Props {
  searchTerm: string;
  refreshKey: number;
}

export const OverviewPanel = ({ searchTerm, refreshKey }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // v0.9.2: 期间选择简化为单 Select —— 「全期間」+ 最近 24 个月，默认当月
  const [period, setPeriod] = useState<string>(dayjs().format('YYYY-MM'));
  const [overview, setOverview] = useState<OverviewResponse | null>(null);

  const periodOptions = useMemo(() => {
    // 起点固定 2026/05（加賀方案上线月），到了下个月自动新增。倒序展示（最新在前）。
    const opts: { value: string; label: string }[] = [{ value: 'all', label: t('dealer.overview_period_all') }];
    const start = dayjs('2026-05-01');
    const now = dayjs();
    let cursor = now.startOf('month');
    let i = 0;
    while (cursor.isAfter(start) || cursor.isSame(start, 'month')) {
      const value = cursor.format('YYYY-MM');
      const tag =
        i === 0
          ? ` · ${t('dealer.overview_period_current_month')}`
          : i === 1
          ? ` · ${t('dealer.overview_period_last_month')}`
          : '';
      opts.push({ value, label: `${cursor.format('YYYY/MM')}${tag}` });
      cursor = cursor.subtract(1, 'month');
      i++;
    }
    return opts;
  }, [t]);

  useEffect(() => {
    const params: OverviewQuery =
      period === 'all' ? { all: true } : { year: Number(period.slice(0, 4)), month: Number(period.slice(5, 7)) };
    getDealersOverview(params).then((r) => setOverview(r.data));
  }, [period, refreshKey]);

  const periodLabel =
    period === 'all'
      ? t('dealer.overview_period_all')
      : overview && overview.year && overview.month
      ? `${overview.year}/${String(overview.month).padStart(2, '0')}`
      : period.replace('-', '/');

  const filtered = useMemo(() => {
    if (!overview) return [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return overview.dealers;
    return overview.dealers.filter(
      (d) =>
        d.code.toLowerCase().includes(q) ||
        d.name.toLowerCase().includes(q) ||
        d.contact_email.toLowerCase().includes(q)
    );
  }, [overview, searchTerm]);

  return (
    <>
      <Group mb="md" gap="md" align="center">
        <Select
          label={t('dealer.overview_period')}
          data={periodOptions}
          value={period}
          onChange={(v) => v && setPeriod(v)}
          leftSection={<IconCalendar size={14} />}
          allowDeselect={false}
          w={260}
          comboboxProps={{ withinPortal: true }}
        />
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} mb="md">
        <KpiCard
          label={t('dealer.overview_active_dealers')}
          value={overview?.kpi.active_dealers ?? 0}
          icon={<IconBuildingStore size={16} />}
        />
        <KpiCard
          label={`${t('dealer.overview_issued')}（${periodLabel}）`}
          value={overview?.kpi.this_month_issued ?? 0}
          icon={<IconReceipt size={16} />}
        />
        <KpiCard
          label={`${t('dealer.overview_contracts')}（${periodLabel}）`}
          value={overview?.kpi.this_month_contracts ?? 0}
          icon={<IconTrendingUp size={16} />}
        />
        <KpiCard
          label={`${t('dealer.overview_incentive')}（${periodLabel}）`}
          value={overview?.kpi.this_month_incentive ?? 0}
          yen
          icon={<IconCoin size={16} />}
        />
      </SimpleGrid>

      <Card withBorder p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('dealer.code')}</Table.Th>
              <Table.Th>{t('dealer.name')}</Table.Th>
              <Table.Th>{t('dealer.contact_email')}</Table.Th>
              <Table.Th>{t('dealer.status')}</Table.Th>
              <Table.Th>{`${t('dealer.overview_issued')}（${periodLabel}）`}</Table.Th>
              <Table.Th>{`${t('dealer.overview_contracts')}（${periodLabel}）`}</Table.Th>
              <Table.Th>{`${t('dealer.overview_incentive')}（${periodLabel}）`}</Table.Th>
              <Table.Th>{t('dealer.action')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.map((d) => (
              <Table.Tr key={d.id}>
                <Table.Td>
                  <Text fw={500}>{d.code}</Text>
                </Table.Td>
                <Table.Td>{d.name}</Table.Td>
                <Table.Td>{d.contact_email}</Table.Td>
                <Table.Td>
                  <Badge color={d.is_active ? 'green' : 'gray'} variant="light">
                    {d.is_active ? t('dealer.active') : t('dealer.inactive')}
                  </Badge>
                </Table.Td>
                <Table.Td>{fmt(d.issued)}</Table.Td>
                <Table.Td>{fmt(d.contracts)}</Table.Td>
                <Table.Td>¥{fmt(d.incentive)}</Table.Td>
                <Table.Td>
                  <ActionIcon variant="light" onClick={() => navigate(`/dealers/${d.id}`)}>
                    <IconEdit size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
            {filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text c="dimmed" ta="center" py="md">
                    {overview ? t('dealer.no_dealers') : ''}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>
    </>
  );
};
